import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CustomerService from '@/services/CustomersServices/CustomerService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    data: null,
    submitting: false,
    errors: null,
    successOnCreate: '',
    loading: false,
    countryList: [],
    companyListOptions: [],
};

export const viewCustomer = createAsyncThunk(
    'customer/viewCustomer',
    async (customerId, { rejectWithValue }) => {
        try {
            const response = await CustomerService.viewCustomer(customerId);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const createCustomer = createAsyncThunk(
    'customer/createCustomer',
    async (data, { rejectWithValue }) => {
        try {
            const { id: customerId } = data;
            let response;
            if (customerId) {
                response = await CustomerService.updateCustomer(data, customerId);
            } else {
                response = await CustomerService.createCustomer(data);
            }
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getCountryList = createAsyncThunk(
    'customer/getCountryList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await CustomerService.getCountryList();
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

export const getCompanyListOptions = createAsyncThunk(
    'customer/getCompanyListOptions',
    async (data, { rejectWithValue }) => {
        try {
            const response = await CustomerService.getCompanyListOptions(data);
            return response.data;
        } catch (error) {
            return rejectWithValue(extractError(error));
        }
    },
);

const createCustomerSlice = createSlice({
    name: 'createCustomer',
    initialState: model,
    reducers: {
        resetCustomerState: (state) => {
            state.data = null;
            state.errors = null;
        },
        resetCustomerStateOnError: (state) => {
            state.errors = null;
        },
        resetCustomerStateOnSuccess: (state) => {
            state.successOnCreate = '';
        },
    },
    extraReducers: (builder) => {
        // view customer
        builder.addCase(viewCustomer.pending, (state) => {
            state.loading = true;
        }).addCase(viewCustomer.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
        }).addCase(viewCustomer.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // create customer
        builder.addCase(createCustomer.pending, (state) => {
            state.submitting = true;
        }).addCase(createCustomer.fulfilled, (state, action) => {
            state.submitting = false;
            const customerId = action.meta.arg.id;
            state.successOnCreate = customerId ? 'Customer updated successfully!' : 'Customer created successfully!';
        }).addCase(createCustomer.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });

        // get country list
        builder.addCase(getCountryList.pending, (state) => {
            state.loading = true;
        }).addCase(getCountryList.fulfilled, (state, action) => {
            state.loading = false;
            state.countryList = action.payload;
        }).addCase(getCountryList.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get company list options
        builder.addCase(getCompanyListOptions.pending, (state) => {
            state.loading = true;
        }).addCase(getCompanyListOptions.fulfilled, (state, action) => {
            state.loading = false;
            state.companyListOptions = action.payload;
        }).addCase(getCompanyListOptions.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });
    },
});

export const {
    resetCustomerState,
    resetCustomerStateOnError,
    resetCustomerStateOnSuccess,
} = createCustomerSlice.actions;

export default createCustomerSlice.reducer;
